import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Check, X } from "lucide-react";
import { openSubscriptionCheckout } from "@/services/paddle";
import {
    type PriceConfig,
    type PricingPlan,
    fetchPricingConfig,
    fetchPricingData
} from "@/services/api";
import toast from "react-hot-toast";
import { trackPlanUpgradeInitiated } from "@/utils/eventTracking";

interface PricingPlansProps {
    userInfo: {
        id: string;
        email: string;
        name: string;
    };
    currentPlan?: string;
    onSuccess?: () => void;
}

export function PricingPlans({ userInfo, currentPlan = 'free', onSuccess }: PricingPlansProps) {
    const [selectedFrequencies, setSelectedFrequencies] = useState<{ [planType: string]: string }>({});
    const [pricingConfigs, setPricingConfigs] = useState<PriceConfig[]>([]);
    const [pricingPlans, setPricingPlans] = useState<PricingPlan[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const loadPricingData = async () => {
            try {
                // Load pricing configs for Paddle IDs
                const configs = await fetchPricingConfig();
                setPricingConfigs(configs);

                // Load pricing plans for display
                const plans = await fetchPricingData();
                // Filter out free plans since this is for subscription
                const paidPlans = plans.filter(plan => plan.plan_type !== 'FREE');
                setPricingPlans(paidPlans);

                // Set default selected frequencies for each plan type
                const defaults: { [planType: string]: string } = {};
                const planTypes = [...new Set(paidPlans.map(plan => plan.plan_type))];

                planTypes.forEach(planType => {
                    const plansForType = paidPlans.filter(plan => plan.plan_type === planType);
                    if (plansForType.length > 0) {
                        // Default to the first frequency available for each plan type
                        defaults[planType] = plansForType[0].frequency;
                    }
                });

                setSelectedFrequencies(defaults);
            } catch (error) {
                console.error('Failed to load pricing data:', error);
                toast.error('Failed to load pricing information');
                setPricingPlans([]);
            } finally {
                setIsLoading(false);
            }
        };

        loadPricingData();
    }, []);

    const handleSubscription = async (planType: 'pro' | 'export') => {
        try {
            // Find the selected plan data for the given plan type
            const plansForType = pricingPlans.filter(plan => plan.plan_type.toUpperCase() === planType.toUpperCase());

            if (plansForType.length === 0) {
                throw new Error(`No pricing configuration available for ${planType} plan`);
            }

            // Get the selected frequency for this plan type
            const selectedFrequencyForPlan = selectedFrequencies[planType.toUpperCase()];

            // Find the specific plan configuration
            const selectedPlanConfig = plansForType.find(plan =>
                plan.frequency === selectedFrequencyForPlan
            ) || plansForType[0];

            if (!selectedPlanConfig) {
                throw new Error(`No pricing configuration found for ${planType} plan`);
            }

            // Track plan upgrade initiated
            trackPlanUpgradeInitiated(currentPlan, planType,selectedFrequencyForPlan);

            // Get price ID from the matching config
            const matchingConfig = pricingConfigs.find(config =>
                config.frequency === selectedPlanConfig.frequency
            );

            if (!matchingConfig) {
                throw new Error('Unable to find matching pricing configuration');
            }

            await openSubscriptionCheckout(
                userInfo.id,
                userInfo.name,
                userInfo.email,
                matchingConfig.paddle_price_id
            );

            // Call success callback if provided
            if (onSuccess) {
                onSuccess();
            }
        } catch (error: any) {
            console.error('Failed to open checkout:', error);
            if (error.message.includes('No pricing configuration')) {
                toast.error('Unable to load subscription plans. Please try again later.');
            } else {
                toast.error('Failed to open checkout. Please try again.');
            }
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-8">
                <p className="text-gray-600">Loading pricing plans...</p>
            </div>
        );
    }

    const plansToRender = pricingPlans;
    const uniquePlanTypes = [...new Set(plansToRender.map(plan => plan.plan_type))];

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {uniquePlanTypes.map((planType) => {
                const planName = plansToRender.find(plan => plan.plan_type === planType)?.display_name;
                const plansForType = plansToRender.filter(plan => plan.plan_type === planType);
                const selectedPlan = plansForType.find(plan =>
                    plan.frequency === selectedFrequencies[planType] || plansForType.length === 1
                ) || plansForType[0];

                if (!selectedPlan) return null;

                const isProPlan = planType === 'PRO';
                const isExportPlan = planType === 'EXPORT';

                // Get plan colors
                const getCardStyle = () => {
                    if (isProPlan) {
                        return "bg-gray-blue border-gray-700";
                    } else {
                        return "bg-gradient-to-br from-gray-900 to-black border-gray-700";
                    }
                };

                const getButtonStyle = () => {
                    if (isProPlan) {
                        return "hover:bg-purple-600 bg-transparent hover:text-white text-white border-white font-semibold";
                    } else {
                        return "bg-white text-black hover:bg-gray-100 font-semibold";
                    }
                };

                const getTextColor = () => {
                    if (isProPlan) {
                        return "text-white";
                    } else {
                        return "text-white";
                    }
                };

                const getPricingTextColor = () => {
                    if (isProPlan) {
                        return {
                            strikethrough: "text-gray-300",
                            main: "text-white",
                            subtitle: "text-gray-300",
                            special: "text-green-300"
                        };
                    } else {
                        return {
                            strikethrough: "text-gray-400",
                            main: "text-white",
                            subtitle: "text-gray-300",
                            special: "text-yellow-300"
                        };
                    }
                };

                const pricingColors = getPricingTextColor();

                const getButtonText = () => {
                    if (isExportPlan) return 'Export Now';
                    return 'Renew Subscription';
                };

                const getPlanTypeParam = (): 'pro' | 'export' => {
                    if (isExportPlan) return 'export';
                    return 'pro';
                };

                return (
                    <div key={planType} className={`relative rounded-2xl p-6 border-2 flex flex-col min-h-[600px] ${getCardStyle()} ${getTextColor()}`}>
                        {/* Tag */}
                        {selectedPlan.tag && (
                            <div className="absolute top-4 right-4">
                                <span className={`text-white text-xs font-bold px-3 py-1 rounded-full ${isProPlan ? 'bg-purple-600' : 'bg-green-500'
                                    }`}>
                                    {selectedPlan.tag}
                                </span>
                            </div>
                        )}

                        <h3 className={`text-2xl font-bold mb-4 capitalize ${getTextColor()}`}>{planName}</h3>

                        {/* Dynamic Frequency Toggle for any plan type OR spacing placeholder */}
                        {plansForType.length > 1 ? (
                            <div className={`flex p-1 mb-4 `}>
                                {plansForType.map((plan) => {
                                    const getFrequencyLabel = (freq: string) => {
                                        switch (freq.toLowerCase()) {
                                            case 'monthly': return 'Monthly';
                                            case 'annually': case 'yearly': return 'Annual';
                                            case 'lifetime': return 'Lifetime';
                                            case 'trial': return 'Trial';
                                            default: return freq.charAt(0).toUpperCase() + freq.slice(1).toLowerCase();
                                        }
                                    };

                                    return (
                                        <button
                                            key={plan.frequency}
                                            className={`flex-1 py-2 px-4 mx-1 border-gray-300 text-sm font-medium  hover:border-purple-500 ${selectedFrequencies[planType] === plan.frequency
                                                ? 'bg-purple-500/60 text-white border-purple-500' : 'bg-transparent text-gray-300 border-gray-300'
                                                }`}
                                            onClick={() => setSelectedFrequencies({
                                                ...selectedFrequencies,
                                                [planType]: plan.frequency
                                            })}
                                        >
                                            {getFrequencyLabel(plan.frequency)}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            // Placeholder div to maintain spacing when no frequency selector is needed
                            <div className="mb-4 py-6"></div>
                        )}

                        {/* Pricing */}
                        <div className="mb-6">
                            <div className="flex items-baseline gap-2">
                                {selectedPlan.discounted_price && selectedPlan.discounted_price !== selectedPlan.price && (
                                    <span className={`text-lg line-through ${pricingColors.strikethrough}`}>
                                        ${selectedPlan.price}
                                    </span>
                                )}
                                <span className={`text-4xl font-bold ${pricingColors.main}`}>
                                    {(selectedPlan.discounted_price || selectedPlan.price) === "0" ||
                                        (selectedPlan.discounted_price || selectedPlan.price) === "0.00"
                                        ? "FREE"
                                        : `$${selectedPlan.discounted_price || selectedPlan.price}`}
                                </span>
                            </div>
                            <div className={`text-sm mt-1 ${pricingColors.subtitle}`}>
                                {selectedPlan.frequency === 'TRIAL' ? '7 days trial' :
                                    selectedPlan.frequency === 'MONTHLY' ? 'per month' :
                                        selectedPlan.frequency === 'ANNUALLY' ? 'per year' :
                                            selectedPlan.frequency === 'LIMITED' ? 'one time' :
                                                selectedPlan.frequency?.toLowerCase() || ''}
                            </div>
                            {selectedPlan.special_text ? (
                                <div className={`text-xs mt-1 ${pricingColors.special}`}>
                                    {selectedPlan.special_text}
                                </div>
                            ) : (
                                // Placeholder div to maintain spacing when no special text
                                <div className="mt-3"></div>
                            )}
                        </div>

                        {/* Features - with flex-grow to push button to bottom */}
                        <div className="flex-grow">
                            <div className="space-y-3 mb-8">
                                {selectedPlan.features.map((feature, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        {feature.included ? (
                                            <Check className="h-5 w-5 text-green-400" />
                                        ) : (
                                            <X className="h-5 w-5 text-gray-500" />
                                        )}
                                        <span className={`text-sm ${feature.included ? getTextColor() : 'text-gray-500'
                                            }`}>
                                            {feature.name}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Button - aligned to bottom */}
                        <div className="mt-auto">
                            <Button
                                className={`w-full ${getButtonStyle()}`}
                                onClick={() => handleSubscription(getPlanTypeParam())}
                            >
                                {getButtonText()}
                            </Button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
} 